/**
 * The first-run walkthrough: a prompt on sign-in, then a spotlight on each control.
 *
 * WHY THIS IS NOT A MODAL ANY MORE
 * --------------------------------
 * It was four screens of prose describing the shape of the app, on the argument that
 * coachmarks pinned to real controls rot. That argument was about the maintenance cost
 * and ignored what the thing is for: somebody signing in for the first time wants to
 * know WHERE TO CLICK, and no amount of well-written prose answers that. A paragraph
 * saying "Mine gathers your tasks" is a sentence to be agreed with; a ring around the
 * Mine tab is an instruction that can be followed.
 *
 * THE ROT IS HANDLED RATHER THAN AVOIDED. Every step names a `data-tour` attribute,
 * not a CSS selector into somebody else's markup. The anchor lives ON the element, so
 * renaming or restyling a tab cannot break it, and DELETING the tab deletes its anchor
 * - which this reads as "skip that step" rather than "ring around nothing". That also
 * makes the steps self-maintaining against things that are conditional: the Guides tab
 * is behind a rollout gate and Reorder is absent with fewer than two projects, and
 * neither needs to be known about here.
 *
 * IT PROMPTS, IT DOES NOT POUNCE. The first screen asks. Somebody signing in to check
 * one date on a Tuesday should be able to say no in one click, and a walkthrough that
 * starts itself over the top of the page is the thing people close without reading.
 *
 * NOTHING IS BLOCKED. The spotlight is `pointer-events: none`, so the control under the
 * ring is live and can actually be pressed. A tutorial about where to click that
 * intercepted the click would be a demonstration of the interface rather than a use of
 * it.
 *
 * Marked seen on the ROSTER ROW rather than in the browser - see tour_seen_at in
 * db/models.py. "Has seen the tour" is a fact about a person, and kept locally it comes
 * back on every new laptop.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { displayHeading, palette, radius, shadow } from '../styles/theme';
import { PrimaryButton, SecondaryButton } from '../styles/ui';
import { type Box, chooseSide, cutout, isVisible, place, type Side } from '../utils/spotlight';

/* ------------------------------------------------------------------ the steps */

interface Step {
  /**
   * The `data-tour` value of the control this points at, or null for a plain card in
   * the middle of the screen - the prompt at the start and the sign-off at the end,
   * neither of which is about one control.
   */
  anchor: string | null;
  title: string;
  body: React.ReactNode;
  /** Where the card prefers to sit. Overridden if it would not fit. */
  side?: Side;
}

/**
 * Ordered as somebody uses the app rather than as the nav is laid out: the row, then
 * the things you do to a row, then the other places work lives.
 *
 * Every one of these says what to PRESS. A step that describes a concept without
 * pointing at a control belongs in the guides, which is what the last card says.
 */
export const STEPS: Step[] = [
  {
    anchor: null,
    title: 'Want a quick tour?',
    body: (
      <>
        <p>
          About a minute. It points at the handful of controls that do most of the work,
          one at a time, and you can stop at any point.
        </p>
        <p>You can keep clicking the page while it is open — nothing is blocked.</p>
      </>
    ),
  },
  {
    anchor: 'lane',
    side: 'right',
    title: 'Every row is a project',
    body: (
      <p>
        Press this triangle to open one. Inside are its phases, milestones and tasks —
        and the rows with <strong>your</strong> work in them are already open, so the
        page starts on what is yours.
      </p>
    ),
  },
  {
    anchor: 'dates',
    side: 'bottom',
    title: 'Move the chart through time',
    body: (
      <p>
        These arrows reach back a month and forward again. The chart opens a fortnight
        before today — and clicking any week in the header drops a line down the whole
        chart.
      </p>
    ),
  },
  {
    anchor: 'add',
    side: 'bottom',
    title: 'Start something new here',
    body: (
      <p>
        <strong>+ Add</strong> makes a project, a future project, or a group. Pick{' '}
        <strong>step by step</strong> the first time and it explains the naming
        convention as you go. You can delete a project you created yourself.
      </p>
    ),
  },
  {
    anchor: 'mine',
    side: 'bottom',
    title: 'What have I got?',
    body: (
      <p>
        <strong>Mine</strong> is the answer, on one page: your tasks, phases, milestones,
        lanes and unread RFCs. It is the tab to check on a Monday.
      </p>
    ),
  },
  {
    anchor: 'board',
    side: 'bottom',
    title: 'Tasks live on the Board',
    body: (
      <p>
        Every ticket and subtask, in columns by status and sectioned by project. Open one
        to assign it, attach a file, or hang it off a phase.
      </p>
    ),
  },
  {
    anchor: 'bugs',
    side: 'bottom',
    title: 'Found something broken?',
    body: (
      <p>
        <strong>Bugs</strong> is the queue. Anybody can file one and set its priority,
        tasks can be attached to it, and whoever closes it gets the points.
      </p>
    ),
  },
  {
    anchor: 'settings',
    side: 'bottom',
    title: 'Yours to change',
    body: (
      <p>
        Your Slack preferences are here, and so is <strong>Projects</strong> — where you
        archive or delete a lane you created, without needing an admin.
      </p>
    ),
  },
  {
    anchor: 'guides',
    side: 'bottom',
    title: 'The rest is written down',
    body: (
      <p>
        A page per area, written alongside the app so it changes in the same commit the
        screens do. Start here when something does not behave the way you expected.
      </p>
    ),
  },
  {
    anchor: null,
    title: 'That is the tour',
    body: (
      <>
        <p>
          Aardvark will message you on Slack from here — a progress nudge on Monday and
          Wednesday, and a question on the day a milestone was dated. Answering there
          writes straight onto the roadmap.
        </p>
        <p>If the roadmap ever disagrees with what you submitted, that is a bug.</p>
      </>
    ),
  },
];

/* ------------------------------------------------------------------ the chrome */

/**
 * The scrim AND the hole, in one element.
 *
 * An enormous spread box-shadow darkens everything outside this rectangle, which is
 * cheaper and sharper than an SVG mask or four separate panels - and leaves the
 * rectangle itself completely untouched, so the control under it looks exactly like
 * itself rather than like a control seen through a tinted sheet.
 *
 * `pointer-events: none` is the important line: the ring is decoration, and the thing
 * it is drawn around stays clickable. See the header.
 */
const Hole = styled.div<{ $box: Box }>`
  position: fixed;
  top: ${(p) => p.$box.top}px;
  left: ${(p) => p.$box.left}px;
  width: ${(p) => p.$box.width}px;
  height: ${(p) => p.$box.height}px;
  z-index: 60;
  border-radius: ${radius.sm};
  border: 2px solid ${palette.deepMagenta};
  box-shadow: 0 0 0 9999px rgba(29, 36, 81, 0.55);
  pointer-events: none;
  transition: top 160ms ease, left 160ms ease, width 160ms ease, height 160ms ease;
`;

/** For the two steps with no anchor: the same darkening, with nothing cut out of it. */
const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(29, 36, 81, 0.55);
  pointer-events: none;
`;

const Card = styled.div<{ $top: number; $left: number; $centred: boolean }>`
  position: fixed;
  z-index: 61;
  ${(p) =>
    p.$centred
      ? `top: 50%; left: 50%; transform: translate(-50%, -50%); width: min(460px, calc(100vw - 24px));`
      : `top: ${p.$top}px; left: ${p.$left}px; width: min(360px, calc(100vw - 24px));`}
  border: 2px solid ${palette.borderStrong};
  border-radius: ${radius.md};
  background: ${palette.cardSolid};
  box-shadow: ${shadow.card};
  padding: 16px 18px 12px;
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
`;

const Title = styled.h2`
  ${displayHeading};
  font-size: 15px;
  color: ${palette.deepMagenta};
  margin: 0;
`;

const Count = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${palette.inkSoft};
`;

/* A real button with an accessible name: an × alone announces as "times". */
const Close = styled.button`
  flex: none;
  border: 0;
  background: none;
  padding: 0 0 0 4px;
  font: inherit;
  font-size: 14px;
  line-height: 1;
  color: ${palette.inkSoft};
  cursor: pointer;

  &:hover {
    color: ${palette.danger};
  }
`;

const Body = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${palette.ink};

  p {
    margin: 0 0 8px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const Spacer = styled.span`
  flex: 1;
`;

const Dots = styled.div`
  display: flex;
  gap: 4px;
`;

const Dot = styled.span<{ $on: boolean }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => (p.$on ? palette.deepMagenta : palette.hairlineStrong)};
`;

/* ---------------------------------------------------------------- the component */

function anchorEl(anchor: string | null): HTMLElement | null {
  if (!anchor) {
    return null;
  }
  /* The FIRST match in document order. Several lanes carry data-tour="lane"; the top
     one is the one to point at, and the DOM already knows which that is. */
  return document.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);
}

function anchorBox(anchor: string | null): Box | null {
  const el = anchorEl(anchor);
  if (!el) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

/**
 * The next step in `direction` that can actually be shown.
 *
 * A step whose control is absent or invisible is skipped rather than drawn: the Guides
 * tab is behind a rollout gate, and Reorder is not there with fewer than two projects.
 * None of that needs to be known here - the DOM is asked, and what is not on screen is
 * not pointed at.
 *
 * Returns null when there is nothing further, which ends the tour.
 */
export function nextShowable(
  steps: readonly Step[],
  from: number,
  direction: 1 | -1,
  canShow: (step: Step) => boolean
): number | null {
  for (let i = from; i >= 0 && i < steps.length; i += direction) {
    if (canShow(steps[i])) {
      return i;
    }
  }
  return null;
}

export interface TourProps {
  /**
   * Called when the tour is finished OR dismissed - both mean "seen", and both mark
   * the roster row.
   *
   * `finished` separates the two, and is used for one thing: whether to follow up by
   * offering to create a project. Somebody who walked the whole thing has consented to
   * being shown things; somebody who pressed ✕ on the prompt has said the opposite, and
   * answering that with a second dialog is how a first run becomes something people
   * warn each other about.
   */
  onDone: (finished: boolean) => void;
}

/** The indices of the steps whose control is on screen right now. */
function showableNow(): number[] {
  return STEPS.map((step, i) => (!step.anchor || isVisible(anchorBox(step.anchor)) ? i : -1)).filter(
    (i) => i >= 0
  );
}

export default function Tour({ onDone }: TourProps) {
  const [step, setStep] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const cardRef = useRef<HTMLDivElement | null>(null);

  /*
    WHICH STEPS THIS READER WILL BE SHOWN, IN STATE RATHER THAN RECOMPUTED PER RENDER.

    Computed once per render it was frozen at whatever the DOM held the first time the
    tour drew - which is while the roadmap is still fetching, so the first card read
    "1 of 7" on a tour with eleven steps and the number never moved. Nothing re-rendered
    the component, so nothing recomputed it.

    So it is refreshed deliberately: on mount, after the scroll settles, on scroll and
    resize, and whenever the DOM changes (see the observer below). That last one is what
    catches the chart arriving a second after the tour did.
  */
  const [showableIdx, setShowableIdx] = useState<number[]>(showableNow);

  const current = STEPS[step];
  const centred = current.anchor === null;

  /*
    Measure, and keep measuring.

    The card's own height decides which side it goes on - the cards are one sentence or
    four, and a fixed estimate puts the short ones too far away and pushes the long ones
    off the screen. So it is read from the rendered element rather than guessed.
  */
  const measure = useCallback(() => {
    if (centred) {
      setBox(null);
      return;
    }
    const found = anchorBox(current.anchor);
    setBox(found);
    if (!isVisible(found)) {
      return;
    }
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const hole = cutout(found as Box, viewport);
    const size = {
      width: cardRef.current?.offsetWidth ?? 360,
      height: cardRef.current?.offsetHeight ?? 160,
    };
    const side = chooseSide(current.side ?? 'bottom', hole, size, viewport);
    setPos(place(side, hole, size, viewport));
  }, [centred, current]);

  /*
    useLayoutEffect rather than useEffect, so the card is positioned in the same frame
    it appears in. With useEffect it paints at the previous step's coordinates first,
    which reads as the card flying across the screen.
  */
  useLayoutEffect(() => {
    /* Bring the control on screen BEFORE measuring, or a step pointing at something
       below the fold rings a rectangle nobody can see. Measured again after the scroll
       settles, because a smooth scroll has not finished when this returns. */
    anchorEl(current.anchor)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    measure();
    const settle = window.setTimeout(measure, 320);
    return () => window.clearTimeout(settle);
  }, [measure, current]);

  /* Once more after the first paint, for the common case: the tour draws, the roadmap
     is still a spinner, and a moment later there is a chart to point at. */
  useEffect(() => {
    const settle = window.setTimeout(() => {
      setShowableIdx((current) => {
        const next = showableNow();
        return next.join() === current.join() ? current : next;
      });
    }, 400);
    return () => window.clearTimeout(settle);
  }, []);

  /*
    Re-measure AND re-check which steps are available.

    The set is only written when it has actually changed. Writing it every time would
    re-render, which mutates the DOM, which fires the observer below - a loop that
    renders for ever while the tour is open.
  */
  const refresh = useCallback(() => {
    measure();
    setShowableIdx((current) => {
      const next = showableNow();
      return next.join() === current.join() ? current : next;
    });
  }, [measure]);

  useEffect(() => {
    /* Capture phase: the chart scrolls inside its own element, and a scroll event on an
       inner node does not bubble to window. */
    window.addEventListener('resize', refresh);
    window.addEventListener('scroll', refresh, true);
    return () => {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('scroll', refresh, true);
    };
  }, [refresh]);

  /*
    The page filling in under the tour.

    A roadmap that is still fetching has no lanes, no + Add and no date arrows, so a
    tour that opened a moment too early would count seven steps out of eleven and skip
    straight past the chart. Watching the document is the only honest answer: there is
    no event for "the page you are annotating has finished arriving".

    Coalesced to one check per frame. Without that, each of the hundreds of nodes the
    chart inserts would schedule its own measure.
  */
  useEffect(() => {
    let queued = 0;
    const observer = new MutationObserver(() => {
      if (queued) {
        return;
      }
      queued = window.requestAnimationFrame(() => {
        queued = 0;
        refresh();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (queued) {
        window.cancelAnimationFrame(queued);
      }
    };
  }, [refresh]);

  /* Escape is how people leave things that cover the screen. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDone(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDone]);

  /* Asked LIVE when moving, rather than from the stored set: between drawing a card
     and pressing Next, a lane may have been collapsed or a section folded away. */
  const canShow = useCallback((s: Step) => !s.anchor || isVisible(anchorBox(s.anchor)), []);

  const go = (direction: 1 | -1) => {
    const found = nextShowable(STEPS, step + direction, direction, canShow);
    if (found === null) {
      /* Walked off the end going forwards, which is finishing. Backwards off the front
         cannot happen: step 0 needs no anchor, so it is always showable. */
      onDone(direction === 1);
      return;
    }
    setStep(found);
  };

  /*
    Numbered over what this reader will actually be shown, not over STEPS.

    "3 of 11" on a deployment where two steps are skipped would be a count that never
    reaches its own total - a small thing that quietly says the app is not sure what it
    is doing.
  */
  const showable = showableIdx.map((i) => STEPS[i]);
  const at = showable.indexOf(current);
  const first = step === 0;
  const last = at === showable.length - 1;

  return (
    <>
      {isVisible(box) ? (
        <Hole $box={cutout(box as Box, { width: window.innerWidth, height: window.innerHeight })} />
      ) : (
        <Scrim />
      )}
      <Card
        ref={cardRef}
        $top={pos.top}
        $left={pos.left}
        $centred={!isVisible(box)}
        role="dialog"
        /* NOT aria-modal. The page underneath stays usable on purpose, and claiming
           otherwise would tell a screen reader to hide everything else. */
        aria-label={current.title}
      >
        <Head>
          <Title>{current.title}</Title>
          <Count>
            {at + 1} of {showable.length}
          </Count>
          <Close type="button" onClick={() => onDone(false)} aria-label="Close the tour">
            ✕
          </Close>
        </Head>

        <Body>{current.body}</Body>

        <Actions>
          <Dots aria-hidden>
            {showable.map((s, i) => (
              <Dot key={s.title} $on={i <= at} />
            ))}
          </Dots>
          <Spacer />
          {first ? (
            <>
              <SecondaryButton type="button" $compact onClick={() => onDone(false)}>
                Not now
              </SecondaryButton>
              <PrimaryButton type="button" $compact onClick={() => go(1)}>
                Show me
              </PrimaryButton>
            </>
          ) : (
            <>
              <SecondaryButton type="button" $compact onClick={() => go(-1)}>
                Back
              </SecondaryButton>
              {last ? (
                <>
                  {/* The last card points at the guides, and going there is not
                      "finished" - the follow-up offer would land on top of the page
                      just asked for. */}
                  <SecondaryButton as={Link} to="/guides" $compact onClick={() => onDone(false)}>
                    Open the guides
                  </SecondaryButton>
                  <PrimaryButton type="button" $compact onClick={() => onDone(true)}>
                    Finish
                  </PrimaryButton>
                </>
              ) : (
                <PrimaryButton type="button" $compact onClick={() => go(1)}>
                  Next
                </PrimaryButton>
              )}
            </>
          )}
        </Actions>
      </Card>
    </>
  );
}
